import { SubmitHandler, useForm } from '@agrotoken-tech/ui';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { useMutation, useQuery } from '@tanstack/react-query';
import { upsertProductiveFacility } from '../../../../../services/productive-facilities/upsertProductiveFacility';
import { getProvinces } from '../../../../../services/provinces/getProvinces';
import { useEffect, useMemo } from 'react';
import { getProvinceLocalities } from '../../../../../services/provinces/getLocalities';
import {
  CommonProductiveFacilitySerializer,
  LocalitySerializer,
} from '@bcba/serializers';
import { CommonUpsertProductiveFacilityDto } from '../../../../../services/types/query-params';
import { useTranslation } from '@bcba/react-hooks';

const resolver = classValidatorResolver(CommonUpsertProductiveFacilityDto);

interface Props {
  onSuccess: () => void;
  selectedFacility?: CommonProductiveFacilitySerializer;
}

export const useUpsertProductiveFacility = ({
  selectedFacility,
  onSuccess,
}: Props) => {
  const { t } = useTranslation();
  const { data: provinces = [] } = useQuery({
    queryKey: ['provinces'],
    queryFn: async () => getProvinces(),
  });

  const { mutate, isPending: isPendingUpsertFacility } = useMutation({
    mutationFn: (data: CommonUpsertProductiveFacilityDto) =>
      upsertProductiveFacility(data),
    onSuccess,
  });

  const onSubmit: SubmitHandler<CommonUpsertProductiveFacilityDto> = (
    data: CommonUpsertProductiveFacilityDto,
  ) => {
    mutate(data);
  };

  const defaultValues = {
    uuid: selectedFacility?.uuid,
    name: selectedFacility?.name,
    ownershipType: selectedFacility?.ownershipType,
    provinceUuid: selectedFacility?.province?.uuid,
    localityUuid: selectedFacility?.locality?.uuid,
    geolocationDocument: {
      uuid: selectedFacility?.geolocationDocument?.uuid,
      keepOriginal: true,
    },
  };

  const formMethods = useForm<CommonUpsertProductiveFacilityDto>({
    resolver,
    defaultValues,
  });
  const {
    setError,
    setValue,
    formState: { isDirty },
  } = formMethods;

  const provincesOptions = useMemo(
    () =>
      provinces?.map(({ name, uuid }) => ({
        label: name,
        value: uuid,
      })) ?? [],
    [provinces],
  );

  const selectedProvinceUuid = formMethods.watch('provinceUuid');
  const hasSelectedProvince = Boolean(selectedProvinceUuid);

  const selectedLocalityUuid = formMethods.watch('localityUuid');

  const getProvinceLocalitiesOptions = (searchValue: string) =>
    hasSelectedProvince
      ? getProvinceLocalities(String(selectedProvinceUuid), searchValue).then(
          (res) => {
            const result = res.map((locality: LocalitySerializer) => ({
              label: locality.name,
              value: String(locality.uuid),
            }));
            return result;
          },
        )
      : Promise.resolve([]);

  useEffect(() => {
    if (selectedProvinceUuid && selectedLocalityUuid && isDirty) {
      setValue('localityUuid', '');
      setError('localityUuid', {
        type: 'custom',
        message: t('companyContactInformation.form.error.validLocality'),
      });
    }
  }, [selectedProvinceUuid]);

  return {
    formMethods,
    onSubmit,
    provincesOptions,
    selectedProvinceUuid,
    hasSelectedProvince,
    getProvinceLocalitiesOptions,
    isPendingUpsertFacility,
  };
};
